import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { AiFillHome, AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import {
  FaUser,
  FaQuestion,
  FaExchangeAlt,
  FaBlog,
  FaAngleDown,
  FaPercent,
  FaTools,
} from "react-icons/fa";
import { BiSolidCategory } from "react-icons/bi";
import { RiFileTextLine } from "react-icons/ri";
import { LuContact, LuEye, LuEyeClosed } from "react-icons/lu";
import { HiOutlineShieldCheck } from "react-icons/hi2";
import { IoGameControllerOutline } from "react-icons/io5";
import { Formik } from "formik";
import * as Yup from "yup";
import { Modal } from "@mui/material";

import { changePassword, getUserById } from "../Redux/Slice/user.slice";
import { logoutUser } from "../Redux/Slice/auth.slice";
import { fetchRolePermissions } from "../Redux/Slice/permission.slice";
import { decryptData } from "../Utils/encryption";
import BackgroundColor from "../component/BackgroundColor";
import { VscLiveShare } from "react-icons/vsc";

const sidebarVariants = {
  open: { x: 0, opacity: 1, transition: { type: "spring", stiffness: 300, damping: 30 } },
  closed: { x: "-100%", opacity: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
};

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userId = localStorage.getItem("yoyouserId");
  const user = useSelector((state) => state.auth.user);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownRef = useRef(null);

  const memoizedUser = useMemo(() => user, [user]);
  const rolePermissions = useSelector((state) => state.permission.rolePermissions);
  const loggedInUserRole = localStorage.getItem("role") || user?.role || "admin";
  const userEmail = user?.email ? decryptData(user.email) : "";
  const isSuperAdmin = userEmail === "admin@gmail.com" || loggedInUserRole === "superadmin";

  useEffect(() => {
    if (userId) {
      dispatch(getUserById(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (loggedInUserRole && !isSuperAdmin) {
      dispatch(fetchRolePermissions(loggedInUserRole));
    }
  }, [dispatch, loggedInUserRole, isSuperAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      if (userId) {
        await dispatch(logoutUser({ userId }));
      }
      navigate("/");
      sessionStorage.removeItem("userId");
      sessionStorage.removeItem("token");
    } catch (error) {
      console.error(error);
    }
  };

  const pages = [
    { title: "Dashboard", icon: <AiFillHome />, path: "/admin" },
    { title: "Category", icon: <BiSolidCategory />, path: "/admin/category" },
    { title: "Games", icon: <IoGameControllerOutline />, path: "/admin/games" },
    { title: "User", icon: <FaUser />, path: "/admin/user" },
    { title: "Orders", icon: <FaExchangeAlt />, path: "/admin/order" },
    { title: "Blog", icon: <FaBlog />, path: "/admin/blog" },
    { title: "Discounts", icon: <FaPercent />, path: "/admin/discounts" },
    { title: "Contact", icon: <LuContact />, path: "/admin/contact" },
    { title: "Bundles", icon: <BiSolidCategory />, path: "/admin/bundles" },
    { title: "Terms & Conditions", icon: <RiFileTextLine />, path: "/admin/terms-conditions" },
    { title: "Privacy Policy", icon: <HiOutlineShieldCheck />, path: "/admin/privacy-policy" },
    { title: "FAQ", icon: <FaQuestion />, path: "/admin/faq" },
    {
      title: "Subscriber",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" width="1em" height="1em">
          <path d="M401.16 40.539H300.366c-5.523 0-10 4.478-10 10s4.477 10 10 10H401.16c24.273 0 47.083 9.446 64.229 26.602 17.16 17.16 26.611 39.97 26.611 64.228 0 50.089-40.75 90.84-90.84 90.84H283.052c-1.892-15.093-15.027-27.453-30.466-27.318-15.491.038-28.777 11.852-30.37 27.318H110.84c-24.267 0-47.077-9.45-64.229-26.611C29.451 198.438 20 175.627 20 151.369c0-50.084 40.75-90.83 90.84-90.83h100.877c5.523 0 10-4.478 10-10s-4.477-10-10-10H110.84C49.723 40.539 0 90.257 0 151.369c0 29.601 11.531 57.433 32.467 78.369 20.928 20.939 48.761 32.471 78.373 32.471h111.208l.007 58.765c-19.026 4.223-33.059 20.11-33.059 39.937v43.027c0 10.503 3.971 20.558 10.898 27.604l35.13 36.821c3.813 3.996 10.143 4.145 14.138.332 3.996-3.812 4.145-10.143.333-14.139l-35.187-36.88-.125-.128c-3.297-3.335-5.188-8.296-5.188-13.61V360.91c0-8.568 5.117-16.001 13.057-19.095v16.835c0 5.522 4.477 10 10 10s10-4.478 10-10l-.003-113.159c-.005-6.77 5.342-10.516 10.398-10.604 5.988-.138 10.839 5.315 10.839 11.134v71.584c0 5.522 4.477 10 10 10s10-4.478 10-10c0-6.301 4.523-10.598 10.778-10.611 5.764.058 10.454 4.942 10.454 10.889v9.669a10 10 0 0 0 9.528 9.989c5.349.231 9.924-3.731 10.428-9.046.655-6.92 4.134-10.765 11.083-11.556 5.666.234 10.204 4.906 10.204 10.612v16.279a10 10 0 0 0 9.528 9.989c5.361.232 9.924-3.731 10.428-9.046.725-7.654 3.645-10.729 11.093-11.556 5.659.225 10.193 4.899 10.193 10.612v33.164c0 18.621-4.38 37.273-12.671 53.949l-12.934 26.072c-2.454 4.948-.432 10.948 4.516 13.402a10.001 10.001 0 0 0 13.402-4.516l12.929-26.062c9.655-19.42 14.758-41.151 14.758-62.846V343.83c0-16.885-13.737-30.621-30.621-30.621-4.277 0-8.917 1.272-12.814 2.966-4.527-11.258-15.565-19.234-28.429-19.245-5.223 0-10.94 1.767-15.47 4.307-5.395-8.467-14.761-14.137-25.381-14.241-3.862-.053-7.557.553-11 1.749V262.21H401.16c61.117 0 110.84-49.723 110.84-110.84 0-29.601-11.531-57.433-32.467-78.369-20.922-20.934-48.755-32.462-78.373-32.462z" />
        </svg>
      ),
      path: "/admin/subscriber",
    },
    { title: "System Settings", icon: <FaTools />, path: "/admin/maintenance" },
    // { title: "Restore Games", icon: <IoGameControllerOutline />, path: "/admin/restore-games" },
  ];

  const allowedPages = isSuperAdmin 
    ? pages 
    : pages.filter(p => {
        const allowed = rolePermissions[loggedInUserRole];
        if (!allowed) return false; // Only show pages whose permissions are explicitly loaded/given
        return allowed.includes(p.title);
      });

  const NavItem = ({ page }) => {
    const isActive = location.pathname === page.path;
    return (
      <motion.div
        whileHover={{ x: 4 }}
        whileTap={{ scale: 0.98 }}
        className="px-4 py-1"
      >
        <button
          onClick={() => {
            navigate(page.path);
            setMobileOpen(false);
          }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${isActive
            ? "text-white"
            : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
        >
          {isActive && (
            <motion.div
              layoutId="activeNav"
              className="absolute inset-0 bg-gradient-primary opacity-100 -z-10"
              initial={false}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          )}
          <span className={`text-xl transition-transform duration-300 ${isActive ? "scale-110" : "group-hover:scale-110"}`}>
            {page.icon}
          </span>
          <span className="font-medium tracking-wide font-sans text-sm">{page.title}</span>

          {isActive && (
            <motion.div
              layoutId="glow"
              className="absolute inset-0 bg-primary/20 blur-xl -z-20"
            />
          )}
        </button>
      </motion.div>
    );
  };

  return (
    <div className="flex min-h-screen font-sans text-gray-200">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-72 h-screen sticky top-0 bg-[#0f0f0f]/80 backdrop-blur-xl border-r border-white/5 z-40">
        <div className="p-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3"
            onClick={() => navigate("/admin")}
            style={{ cursor: "pointer" }}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-[0_0_20px_rgba(98,99,237,0.4)]">
              <IoGameControllerOutline className="text-2xl text-white" />
            </div>
            <span className="text-2xl font-bold tracking-wider bg-gradient-primary bg-clip-text text-transparent">
              YOYO
            </span>
          </motion.div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 no-scrollbar">
          {allowedPages.map((page) => (
            <NavItem key={page.path} page={page} />
          ))}
        </nav>

        <div className="p-6 mt-auto">
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300 border border-white/5 font-medium"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Mobile */}
      <motion.aside
        variants={sidebarVariants}
        initial="closed"
        animate={mobileOpen ? "open" : "closed"}
        className="fixed top-0 left-0 bottom-0 w-72 bg-[#0f0f0f] backdrop-blur-2xl border-r border-white/5 z-[60] lg:hidden shadow-2xl flex flex-col"
      >
        <div className="p-6 flex justify-between items-center border-b border-white/5 bg-[#0f0f0f]/30">
          <div
            className="flex items-center gap-3"
            onClick={() => { navigate("/admin"); setMobileOpen(false); }}
            style={{ cursor: "pointer" }}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-primary flex items-center justify-center shadow-[0_0_15px_rgba(98,99,237,0.4)]">
              <IoGameControllerOutline className="text-xl text-white" />
            </div>
            <span className="text-xl font-bold tracking-wider bg-gradient-primary bg-clip-text text-transparent">
              YOYO
            </span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 hover:text-white transition-all border border-white/10 hover:border-white/20 active:scale-95"
          >
            <AiOutlineClose className="text-lg" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar">
          {allowedPages.map((page) => (
            <NavItem key={page.path} page={page} />
          ))}
        </nav>
        <div className="p-6 border-t border-white/5 bg-[#0f0f0f]/30">
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-full py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 hover:border-red-500/30 transition-all font-medium flex items-center justify-center gap-2 active:scale-[0.98]"
          >
            Logout
          </button>
        </div>
      </motion.aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-20 sticky top-0 flex items-center justify-between px-6 md:px-10 bg-[#0f0f0f]/50 backdrop-blur-md border-b border-white/5 z-30">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-xl bg-white/5 lg:hidden"
          >
            <AiOutlineMenu className="text-xl" />
          </button>

          <div className="hidden lg:block">
            <h2 className="text-lg font-semibold text-white/90">
              {pages.find(p => p.path === location.pathname)?.title || "Admin Panel"}
            </h2>
          </div>

          <div className="flex items-center gap-4">

            <Link
              target="_self"
              to="/"
              onClick={() => sessionStorage.setItem('hasRedirected', 'true')}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <VscLiveShare className="text-primary" /> User Panel
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-3 p-1.5 pr-3 rounded-full bg-white/5 hover:bg-white/10 transition-all border border-white/10 group"
              >
                <div className="w-9 h-9 rounded-full overflow-hidden border border-primary/40 group-hover:border-primary transition-colors shadow-[0_0_10px_rgba(98,99,237,0.2)]">
                  {memoizedUser?.photo && memoizedUser?.photo !== "null" ? (
                    <img src={memoizedUser.photo} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/20 text-primary font-bold">
                      {decryptData(memoizedUser?.userName)?.split(" ").map(n => n[0]).join("").toUpperCase() || "A"}
                    </div>
                  )}
                </div>
                <div className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-sm font-semibold text-white">{decryptData(user?.userName)}</span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Administrator</span>
                </div>
                <FaAngleDown className={`text-xs text-gray-500 transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-3 w-56 bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl"
                  >
                    <div className="p-2">
                      <button
                        onClick={() => { navigate("/admin/profile"); setDropdownOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                      >
                        <FaUser className="text-primary" /> Profile
                      </button>
                      <button
                        onClick={() => { setOpenPassword(true); setDropdownOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                      >
                        <LuEye className="text-secondary" /> Change Password
                      </button>
                      <div className="h-px bg-white/5 my-1 mx-2" />
                      <button
                        onClick={() => { setShowLogoutModal(true); setDropdownOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                      >
                        <FaExchangeAlt /> Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Main Viewport */}
        <main className="flex-1 relative overflow-x-hidden overflow-y-auto">
          <BackgroundColor>
            <div className="p-6 md:p-10 pb-20">
              {children}
            </div>
          </BackgroundColor>
        </main>
      </div>

      {/* Change Password Modal */}
      <Modal
        open={openPassword}
        onClose={() => setOpenPassword(false)}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 text-white w-[90%] max-w-[400px] shadow-2xl">
          <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
            <h1 className="text-lg font-semibold tracking-tight">Change Password</h1>
            <button onClick={() => setOpenPassword(false)} className="text-gray-400 hover:text-white transition-colors">
              <AiOutlineClose className="text-xl" />
            </button>
          </div>
          <Formik
            initialValues={{ oldPassword: "", newPassword: "", confirmNewPassword: "", showOldPassword: false, showNewPassword: false, showConfirmNewPassword: false }}
            validationSchema={Yup.object({
              oldPassword: Yup.string().required("Required"),
              newPassword: Yup.string().min(6, "Must be at least 6 characters").required("Required"),
              confirmNewPassword: Yup.string().oneOf([Yup.ref('newPassword'), null], 'Passwords must match').required("Required"),
            })}
            onSubmit={(values) => {
              dispatch(changePassword({ email: user.email, oldPassword: values.oldPassword, newPassword: values.newPassword }))
                .then((res) => { if (res.payload.success) setOpenPassword(false); });
            }}
          >
            {({ handleChange, handleSubmit, values, errors, touched, setFieldValue }) => (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Old Password</label>
                  <div className="relative">
                    <input
                      type={values.showOldPassword ? "text" : "password"}
                      name="oldPassword"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                      value={values.oldPassword}
                      onChange={handleChange}
                    />
                    <button type="button" className="absolute right-4 top-3.5 text-gray-500" onClick={() => setFieldValue("showOldPassword", !values.showOldPassword)}>
                      {values.showOldPassword ? <LuEye /> : <LuEyeClosed />}
                    </button>
                  </div>
                  {errors.oldPassword && touched.oldPassword && <span className="text-red-400 text-xs">{errors.oldPassword}</span>}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">New Password</label>
                  <div className="relative">
                    <input
                      type={values.showNewPassword ? "text" : "password"}
                      name="newPassword"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                      value={values.newPassword}
                      onChange={handleChange}
                    />
                    <button type="button" className="absolute right-4 top-3.5 text-gray-500" onClick={() => setFieldValue("showNewPassword", !values.showNewPassword)}>
                      {values.showNewPassword ? <LuEye /> : <LuEyeClosed />}
                    </button>
                  </div>
                  {errors.newPassword && touched.newPassword && <span className="text-red-400 text-xs">{errors.newPassword}</span>}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={values.showConfirmNewPassword ? "text" : "password"}
                      name="confirmNewPassword"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                      value={values.confirmNewPassword}
                      onChange={handleChange}
                    />
                    <button type="button" className="absolute right-4 top-3.5 text-gray-500" onClick={() => setFieldValue("showConfirmNewPassword", !values.showConfirmNewPassword)}>
                      {values.showConfirmNewPassword ? <LuEye /> : <LuEyeClosed />}
                    </button>
                  </div>
                  {errors.confirmNewPassword && touched.confirmNewPassword && <span className="text-red-400 text-xs">{errors.confirmNewPassword}</span>}
                </div>

                <div className="flex gap-4 mt-4">
                  <button type="button" onClick={() => setOpenPassword(false)} className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">Cancel</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl bg-gradient-primary text-white font-bold shadow-[0_0_20px_rgba(98,99,237,0.3)]">Update</button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Modal>

      {/* Logout Modal */}
      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 text-center w-[90%] max-w-[360px] shadow-2xl">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-6 text-2xl border border-red-500/20">
            <FaExchangeAlt />
          </div>
          <h2 className="text-xl font-bold mb-2">Are you sure?</h2>
          <p className="text-gray-400 text-sm mb-8">You will need to login again to access the admin panel.</p>
          <div className="flex gap-4">
            <button onClick={() => setShowLogoutModal(false)} className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">No, stay</button>
            <button onClick={handleLogout} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold shadow-[0_0_20px_rgba(239,68,68,0.3)]">Yes, Logout</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;
